import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FoodImageGallery from '@/components/features/Menu/FoodImageGallery';
import FoodInfoBox from '@/components/features/Menu/FoodInfoBox';
import { trackEvent } from '@/Services/analytics';
import { getFoodBySlug, toMenuItem } from '@/data/foods';
import { menuPage } from '@/data/content';
import SeoMeta from '@/components/seo/SeoMeta';
import Button from '@/components/ui/Button';
import { SpinnerIcon } from '@/components/ui/Icons';

interface FoodDetailData {
    id: number;
    name: string;
    item_name: string;
    title: string;
    price?: number;
    description: string;
    images: string[];
    allergens: string[];
    seo?: {
        title?: string;
        description?: string;
        canonical?: string;
    };
}

const FoodDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const [food, setFood] = useState<FoodDetailData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchFoodData = () => {
            setLoading(true);
            setError(null);

            const staticFood = slug ? getFoodBySlug(slug) : undefined;
            if (staticFood) {
                const mapped = toMenuItem(staticFood);
                setFood({
                    ...mapped,
                    title: mapped.item_name,
                    images: mapped.item_image ? [mapped.item_image] : [],
                    allergens: mapped.allergens,
                });
            } else {
                setError(menuPage.coming_soon);
            }
            setLoading(false);
        };

        if (slug) {
            fetchFoodData();
        } else {
            setLoading(false);
        }
    }, [slug, navigate]);

    useEffect(() => {
        if (!food) return;
        trackEvent('item_view', { item_id: food.id, item_name: food.title, value: Number(food.price || 0) });
    }, [food]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <SpinnerIcon className="w-10 h-10 text-app-primary" />
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{menuPage.loading_details}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="flex flex-col items-center text-center gap-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-8 max-w-sm shadow-sm">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-app-text dark:text-white mb-1">{menuPage.load_error}</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
                    </div>
                    <Button variant="primary" size="md" onClick={() => navigate('/menu')}>
                        {menuPage.back_to_menu}
                    </Button>
                </div>
            </div>
        );
    }

    if (!food) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <p className="text-sm text-gray-400 dark:text-gray-500">{menuPage.not_found}</p>
            </div>
        );
    }

    return (
        <>
            <SeoMeta
                page="menu"
                overrides={{
                    title: food?.seo?.title || `${food?.title || 'Plat'} | Bivin's Kitchen`,
                    description: food?.seo?.description || food?.description || menuPage.food_seo_description_fallback,
                }}
            />
            <div className="food-detail-page">
                <h1 className="sr-only">{menuPage.page_title}</h1>
            <div className="food-detail-inner">
                {/* Back + breadcrumb */}
                <div className="food-detail-header">
                    <Button
                        variant="ghost"
                        type="button"
                        onClick={() => navigate('/menu')}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-app-primary transition-colors mb-4"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        {menuPage.back_to_menu}
                    </Button>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                        <span>Menu</span>
                        <span>›</span>
                        <span className="text-app-text dark:text-white font-medium">{food.title}</span>
                    </div>
                </div>

                <div className="food-detail-grid compact">
                    <div className="food-gallery-section compact">
                        <FoodImageGallery images={food.images} title={food.title} compact={true} />
                    </div>
                    <div className="food-info-section compact">
                        <FoodInfoBox food={food} compact={true} />
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default FoodDetailPage;
